/**
 * An uploader with multipart, resumption support.
 */

import { RpxResponse, rpx } from './rpx';
import { stripSVG } from 'shared/media';

export type UploadState = {
  status: 'init' | 'uploading' | 'finishing' | 'done' | 'failed';
  progress: number;
};

export type UploadApi = Pick<typeof rpx.files, 'beginUpload' | 'finishUpload'>;

type UploadOpts = {
  file: Blob;
  name?: string;
  isPublic?: boolean;
  folderId?: string;
  courseId?: UUID;
  onProgress(state: UploadState): void;
  api?: UploadApi;
};

type UploadContext = RpxResponse<typeof rpx.files.beginUpload> & {
  /**
   * A map of upload part index to etag.
   */
  etags: Map<number, string>;
  file: Blob;
  name: string;
  parts: Array<{ partNumber: number; url: string }>;
  terminated: boolean;
};

/**
 * In a multipart upload, this uploads the specified part.
 */
async function uploadPart(opts: {
  file: Blob;
  partSize: number;
  partNumber: number;
  url: string;
  signal: AbortSignal;
}) {
  const start = opts.partNumber * opts.partSize;
  const end = Math.min(start + opts.partSize, opts.file.size);
  const body = opts.file.slice(start, end);
  const numRetries = 3;
  let error: any;
  for (let i = 0; i < numRetries; ++i) {
    try {
      const response = await fetch(opts.url, {
        method: 'PUT',
        headers: {
          'Content-Type': opts.file.type,
          'Content-Length': body.size.toString(),
        },
        body,
        signal: opts.signal,
      });
      // ETag is wrapped in quotes, but the finalization call needs this to be unquoted
      // so, slice(1, -1) removes the quotes.
      const etag = response.headers.get('ETag')?.slice(1, -1) || '';
      const text = await response.text();
      if (response.status >= 400) {
        throw new Error(text);
      }
      return etag;
    } catch (err) {
      if (opts.signal.aborted) {
        throw err;
      }
      error = err;
      console.error(err);
    }
  }
  throw error;
}

function getFileName(blob: Blob, defaultName = 'unnamedfile') {
  return (blob as File).name || defaultName;
}

async function sanitizeFile(blob: Blob) {
  if (!blob.type.toLowerCase().includes('svg')) {
    return blob;
  }
  const svg = await blob.text();
  const stripped = stripSVG(svg);
  return new File([stripped], getFileName(blob, 'sanitized.svg'), { type: blob.type });
}

function getProgress(ctx: UploadContext) {
  return ctx.presignedUrls.length ? (ctx.etags.size / ctx.presignedUrls.length) * 100 : 0;
}

async function makeUploadContext(opts: UploadOpts): Promise<UploadContext> {
  const file = await sanitizeFile(opts.file);
  const name = opts.name || getFileName(opts.file);
  opts.onProgress({ status: 'init', progress: 0 });
  const ctx = await (opts.api ?? rpx.files).beginUpload({
    size: file.size,
    type: file.type,
    name,
  });
  return {
    ...ctx,
    terminated: false,
    file,
    name,
    parts: ctx.presignedUrls.map((url, partNumber) => ({ url, partNumber })),
    etags: new Map(),
  };
}

async function uploadParts(opts: UploadOpts, ctx: UploadContext, signal: AbortSignal) {
  while (ctx.parts.length && !ctx.terminated) {
    const part = ctx.parts.shift();
    if (part == null) {
      break;
    }
    try {
      const etag = await uploadPart({
        ...part,
        file: ctx.file,
        partSize: ctx.partSize,
        signal,
      });
      ctx.etags.set(part.partNumber + 1, etag);
      opts.onProgress({
        status: ctx.terminated ? 'failed' : 'uploading',
        progress: getProgress(ctx),
      });
    } catch (err) {
      ctx.parts.push(part);
      if (!ctx.terminated) {
        ctx.terminated = true;
        throw err;
      }
    }
  }
}

/**
 * The course whose page the upload starts on, so the server can charge the
 * upload to that course's storage. Student URLs prefix the id with a slug.
 */
export function courseIdFromPath(pathname: string) {
  return pathname.match(
    /(?:^|\/)(?:courses|products)\/(?:[^/]*?)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i,
  )?.[1] as UUID | undefined;
}

/**
 * Create an uploader object which handles progress, resumption, and can abort a multipart
 * upload prior to completion.
 */
export function makeUploader(opts: UploadOpts) {
  const courseId =
    opts.courseId ??
    (typeof location === 'undefined' ? undefined : courseIdFromPath(location.pathname));
  let rejection: unknown;
  const parallelism = 4;
  let uploadContext: UploadContext | undefined;
  let activeController: AbortController | undefined;
  let abortRequested = false;

  return {
    async upload() {
      if (rejection) {
        throw rejection;
      }
      abortRequested = false;
      activeController = new AbortController();
      uploadContext ||= await makeUploadContext(opts);
      const ctx = uploadContext;
      if (abortRequested) {
        ctx.terminated = true;
        return;
      }
      ctx.terminated = false;
      opts.onProgress({ status: 'uploading', progress: getProgress(ctx) });
      const partResults = await Promise.allSettled(
        new Array(parallelism).fill(0).map(() => uploadParts(opts, ctx, activeController!.signal)),
      );
      const failedPart = partResults.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (failedPart) {
        throw failedPart.reason;
      }
      if (ctx.terminated) {
        return;
      }
      opts.onProgress({ status: 'finishing', progress: 100 });
      const result = await (opts.api ?? rpx.files)
        .finishUpload({
          fileId: ctx.fileId,
          type: ctx.file.type,
          name: ctx.name,
          isPublic: opts.isPublic,
          folderId: opts.folderId,
          courseId,
          uploadId: ctx.uploadId,
          filePath: ctx.filePath,
          etags: Array.from(ctx.etags.entries())
            .map(([k, v]) => ({ part: k, etag: v }))
            .sort((a, b) => a.part - b.part),
        })
        .catch((err) => {
          if (err?.data?.type === 'maxSizeExceeded') {
            rejection = err;
          }
          throw err;
        });
      // Clear upload info before we notify of completion. This way, abort is a noop
      ctx.terminated = true;
      opts.onProgress({ status: 'done', progress: 100 });
      return {
        name: getFileName(ctx.file, ctx.name),
        type: ctx.file.type,
        size: ctx.file.size,
        ...result,
      };
    },

    async abort() {
      abortRequested = true;
      activeController?.abort();
      if (uploadContext) {
        uploadContext.terminated = true;
      }
    },
  };
}
